import { PROFILE_CONSTANTS } from '../constants/profileConstants';

/**
 * Validates and ensures data types match database schema requirements
 * @param {string} field - Field name
 * @param {any} value - Value to validate
 * @returns {any} - Value with correct data type
 */
export const validateDataType = (field, value) => {
  // Skip null/undefined values
  if (value === null || value === undefined) {
    return null;
  }
  
  // Handle array fields
  if (PROFILE_CONSTANTS.ARRAY_FIELDS?.includes(field)) {
    // Ensure value is an array
    if (!Array.isArray(value)) {
      // Convert string to array if possible
      if (typeof value === 'string') {
        return value.split(',').map(item => item.trim()).filter(Boolean);
      }
      // Return empty array for invalid values
      return [];
    }
    return value;
  }
  
  // Handle numeric fields
  if (PROFILE_CONSTANTS.NUMERIC_FIELDS?.includes(field)) {
    const numValue = parseFloat(value);
    return !isNaN(numValue) ? numValue : null;
  }
  
  // Handle integer fields
  if (PROFILE_CONSTANTS.INTEGER_FIELDS?.includes(field)) {
    const intValue = parseInt(value, 10);
    return !isNaN(intValue) ? intValue : 0;
  }
  
  // Handle URL fields
  if (PROFILE_CONSTANTS.URL_FIELDS?.includes(field)) {
    if (typeof value !== 'string') {
      return '';
    }
    return value;
  }
  
  // Handle string fields
  if (typeof value !== 'string' && typeof value !== 'object') {
    return String(value);
  }
  
  return value;
};

/**
 * Normalizes array fields to ensure they're always arrays of strings
 * This fixes the [object Object] display issue
 */
export const normalizeArrayField = (value) => {
  if (!value) return [];
  
  // If it's already an array
  if (Array.isArray(value)) {
    // Special case for work_experience - preserve the whole array
    if (value.length > 0 && typeof value[0] === 'object' && 
        (value[0].position !== undefined || value[0].company !== undefined)) {
      // Preserve work_experience array as JSONB
      // console.log('Preserving work_experience array:', value);
      return value;
    }
    
    // Special case for security_questions - preserve the object structure
    if (value.length > 0 && typeof value[0] === 'object' && 
        (value[0].question !== undefined || value[0].answer !== undefined)) {
      // Preserve security_questions array as JSONB
      return value;
    }

    // Special case for languages - preserve the object structure
    if (value.length > 0 && typeof value[0] === 'object' && 
        (value[0].name !== undefined && value[0].level !== undefined)) {
      // Preserve languages array as JSONB with name and level
      return value;
    }

    // Special case for certifications - preserve the object structure
    if (value.length > 0 && typeof value[0] === 'object' && 
        (value[0].name !== undefined)) {
      // Preserve certifications array as JSONB with name and url
      return value;
    }
    
    return value.map(item => {
      if (item === null || item === undefined) return '';
      
      // Handle objects - convert to string safely
      if (typeof item === 'object') {
        // For language objects with name/level structure
        if (item.name && item.level) {
          return item; // Keep language objects intact
        }
        // For other objects, convert to JSON string for meaningful representation
        return typeof item.toString === 'function' && item.toString !== Object.prototype.toString ? item.toString() : JSON.stringify(item);
      }
      
      return String(item);
    }).filter(item => item !== '');
  }
  
  // If it's a string, split by comma
  if (typeof value === 'string') {
    return value.split(',').map(item => typeof item === 'string' ? item.trim() : item).filter(item => item !== '');
  }
  
  // If it's an object, try to convert
  if (typeof value === 'object') {
    return [value.toString ? value.toString() : JSON.stringify(value)];
  }
  
  // Fallback to string conversion
  return [String(value)];
};

/**
 * Serializes profile data for database submission
 * Handles proper type conversion and array formatting
 */
export const serializeProfileData = (profileData) => {
  const processedData = {};
  
  // Define the known fields in the user_profiles table schema
  const knownDatabaseFields = [
    // Core fields
    'id', 'email', 'full_name', 'role', 'profile_picture', 'bio', 'phone',
    'linkedin', 'title', 'location', 'timezone', 'education', 'portfolio_description',
    'website', 'company_name', 'created_at', 'updated_at',
    
    // Numeric fields
    'hourly_rate', 'minimum_rate', 'maximum_rate', 'experience_years',
    
    // Array/JSONB fields
    'skills', 'languages', 'certifications', 'soft_skills', 'work_samples',
    'preferred_project_types', 'other_profiles', 'assessment_scores',
    'availability', 'portfolio_links', 'work_experience', 'security_questions',
    
    // Status fields
    'is_verified', 'is_active', 'availability_status', 'preferred_work_type',
    'communication_style', 'industry_specialization', 'github_url', 'experience_level',
    
    // New fields
    'years_of_experience_category', 'availability_hours', 'portfolio_url', 'video_introduction_url',
    
    // Currency preference
    'preferred_currency',
    
    // Work style fields
    'work_pace', 'feedback_preference', 'collaboration_style', 'problem_solving_approach',
    'work_style_description',
    
    // Settings fields (JSONB)
    'notification_settings', 'privacy_settings', 'security_settings'
  ];
  
  Object.keys(profileData).forEach(key => {
    // Skip fields not in the database schema
    if (!knownDatabaseFields.includes(key)) {
      // Skip non-database fields
      // console.log(`Skipping non-database field: ${key}`);
      return;
    }
    
    const value = profileData[key];
    
    // Skip null/undefined values
    if (value === null || value === undefined) {
      processedData[key] = null;
      return;
    }
    
    // Validate data type
    const validatedValue = validateDataType(key, value);
    
    // Handle URL fields (including video_introduction_url)
    if (PROFILE_CONSTANTS.URL_FIELDS?.includes(key)) {
      // Validate and clean URL fields
      if (typeof validatedValue === 'object' && validatedValue !== null) {
        processedData[key] = '';
      } else {
        processedData[key] = String(validatedValue || '');
      }
      return; // Early return to skip array field handling
    }
    
    // Handle array fields
    if (PROFILE_CONSTANTS.ARRAY_FIELDS?.includes(key)) {
      const normalizedArray = normalizeArrayField(validatedValue);
      
      // Special handling for languages (keep as objects)
      if (key === 'languages') {
        processedData[key] = normalizedArray.map(item => {
          if (typeof item === 'object' && item.name && item.level) {
            return {
              name: String(item.name || ''),
              level: String(item.level || 'conversational')
            };
          }
          // Convert string back to basic language object
          return {
            name: String(item),
            level: 'conversational'
          };
        });
      }
      // Special handling for certifications (keep as objects)
      else if (key === 'certifications') {
        processedData[key] = normalizedArray.map(item => {
          if (typeof item === 'object' && item.name) {
            return {
              name: String(item.name || ''),
              url: item.url ? String(item.url) : null
            };
          }
          // Convert string to certification object
          return {
            name: String(item),
            url: null
          };
        });
      } 
      // Special handling for work_experience
      else if (key === 'work_experience') {
        const cleanExperiences = Array.isArray(validatedValue) ? validatedValue.map(exp => {
          // Only keep essential properties and ensure correct types
          if (exp && typeof exp === 'object') {
            return {
              position: String(exp.position || ''),
              company: String(exp.company || ''),
              location: String(exp.location || ''),
              type: String(exp.type || ''),
              start_date: String(exp.start_date || ''),
              end_date: String(exp.end_date || ''),
              current: Boolean(exp.current),
              description: String(exp.description || ''),
              skills: Array.isArray(exp.skills) ? exp.skills.map(String) : [],
              achievements: Array.isArray(exp.achievements) ? exp.achievements.map(String) : [],
              responsibilities: Array.isArray(exp.responsibilities) ? exp.responsibilities.map(String) : []
            };
          }
          return null;
        }).filter(Boolean) : [];
        
        processedData[key] = cleanExperiences;
      }
      // Special handling for security_questions
      else if (key === 'security_questions') {
        // Ensure security questions are properly formatted with id, question, answer
        const cleanQuestions = Array.isArray(validatedValue) ? validatedValue.map(q => {
          if (q && typeof q === 'object') {
            return {
              id: Number(q.id) || 0,
              question: String(q.question || ''),
              answer: String(q.answer || '')
            };
          }
          return null;
        }).filter(Boolean) : [];
        
        processedData[key] = cleanQuestions;
      }
      // Special handling for portfolio_links
      else if (key === 'portfolio_links') {
        // Ensure portfolio links are properly formatted
        processedData[key] = normalizedArray.map(link => {
          if (typeof link === 'object' && link.url) {
            return {
              url: String(link.url || ''),
              title: String(link.title || ''),
              description: String(link.description || '')
            };
          }
          // Convert string to URL object
          return {
            url: String(link),
            title: '',
            description: ''
          };
        });
      }
      // Special handling for soft_skills
      else if (key === 'soft_skills') {
        // Ensure soft_skills is always an array of strings
        processedData[key] = normalizedArray.map(String);
      }
      // Special handling for work_samples
      else if (key === 'work_samples') {
        // Ensure work_samples is properly formatted
        processedData[key] = normalizedArray.map(sample => {
          if (typeof sample === 'object' && sample.title) {
            return {
              title: String(sample.title || ''),
              description: String(sample.description || ''),
              url: String(sample.url || ''),
              type: String(sample.type || 'other')
            };
          }
          // Convert string to sample object
          return {
            title: String(sample),
            description: '',
            url: '',
            type: 'other'
          };
        });
      }
      // Handle other JSONB array fields
      else if (PROFILE_CONSTANTS.JSON_FIELDS?.includes(key)) {
        processedData[key] = normalizedArray;
      } 
      else {
        // For other arrays, ensure they're arrays of strings
        processedData[key] = normalizedArray.map(String);
      }
    }
    // Handle numeric fields
    else if (['hourly_rate', 'minimum_rate', 'maximum_rate'].includes(key)) {
      // Ensure numeric values are properly parsed
      const numValue = parseFloat(validatedValue);
      processedData[key] = !isNaN(numValue) ? numValue : null;
    }
    else if (['experience_years'].includes(key)) {
      // Ensure integer values are properly parsed
      const intValue = parseInt(validatedValue, 10);
      processedData[key] = !isNaN(intValue) ? intValue : 0;
    }
    // Handle object fields (JSONB)
    else if (typeof validatedValue === 'object' && validatedValue !== null && !Array.isArray(validatedValue)) {
      // Handle settings objects (notification_settings, privacy_settings, security_settings)
      if (key === 'notification_settings' || key === 'privacy_settings' || key === 'security_settings') {
        try {
          // Deep clone to avoid mutation issues and ensure proper JSONB formatting
          processedData[key] = JSON.parse(JSON.stringify(validatedValue));
          
          // Debug logging
          if (key === 'notification_settings' && process.env.NODE_ENV === 'development') {
            console.log('serializeProfileData: Processing notification_settings:', processedData[key]);
          }
        } catch (e) {
          if (process.env.NODE_ENV === 'development') {
            console.error(`Error serializing ${key}:`, e);
          }
          processedData[key] = {};
        }
      }
      // For assessment_scores and availability
      else if (key === 'assessment_scores') {
        // Ensure assessment_scores is a valid object
        try {
          processedData[key] = JSON.parse(JSON.stringify(validatedValue));
        } catch (e) {
          if (process.env.NODE_ENV === 'development') {
            console.error('Error serializing assessment_scores:', e);
          }
          processedData[key] = {};
        }
      } else {
        processedData[key] = validatedValue;
      }
    }
    // Handle all other fields
    else {
      processedData[key] = String(validatedValue || '');
    }
  });
  
  // Add updated timestamp
  processedData.updated_at = new Date().toISOString();
  
  return processedData;
};

/**
 * Safely converts any value to a display string
 * Prevents [object Object] from appearing in UI
 * @param {any} value - The value to convert to a display string
 * @param {string} fieldName - Optional field name for context-specific formatting
 * @returns {string} - A string representation of the value
 */
export const safeDisplayValue = (value, fieldName = '') => {
  if (value === null || value === undefined) return '';
  
  if (typeof value === 'object') {
    // Handle language objects
    if (value.name && value.level) {
      return `${value.name} (${value.level})`;
    }

    // Handle certification objects
    if (fieldName === 'certifications' && value.name) {
      return value.url ? `${value.name}` : value.name;
    }
    
    // Handle work sample objects
    if (fieldName === 'work_samples' && value.title) {
      return value.url ? `${value.title} (${value.url})` : value.title;
    }
    
    // Handle portfolio link objects
    if (fieldName === 'portfolio_links' && value.url) {
      return value.title ? `${value.title}: ${value.url}` : value.url;
    }
    
    // Handle work experience objects
    if (fieldName === 'work_experience' && value.position && value.company) {
      return `${value.position} at ${value.company}${value.start_date ? ` (${value.start_date})` : ''}`;
    }
    
    // Handle assessment scores
    if (fieldName === 'assessment_scores') {
      try {
        return Object.entries(value)
          .map(([key, score]) => `${key}: ${score}`)
          .join(', ');
      } catch (e) {
        return '';
      }
    }
    
    // Handle arrays
    if (Array.isArray(value)) {
      // Special handling for specific array fields
      if (PROFILE_CONSTANTS.ARRAY_FIELDS.includes(fieldName)) {
        return value.map(item => safeDisplayValue(item, fieldName)).join(', ');
      }
      return value.map(safeDisplayValue).join(', ');
    }
    
    // Handle other objects - ensure meaningful string representation
    return typeof value.toString === 'function' && value.toString !== Object.prototype.toString 
      ? value.toString() 
      : JSON.stringify(value);
  }
  
  // Handle numeric fields with proper formatting
  if (PROFILE_CONSTANTS.NUMERIC_FIELDS.includes(fieldName) && !isNaN(parseFloat(value))) {
    return `$${parseFloat(value).toFixed(2)}`;
  }
  
  // Handle URL fields
  if (PROFILE_CONSTANTS.URL_FIELDS.includes(fieldName) && value) {
    try {
      const url = new URL(value.startsWith('http') ? value : `https://${value}`);
      return url.hostname + url.pathname;
    } catch (e) {
      return String(value);
    }
  }
  
  return String(value);
};

/**
 * Validates and cleans form input values
 */
export const cleanFormValue = (value) => {
  if (value === null || value === undefined) {
    return value;
  }
  
  if (typeof value === 'string') {
    return value.trim();
  }
  
  if (typeof value === 'object' && value !== null) {
    // Don't stringify objects, return as-is for proper handling
    return value;
  }
  
  return value;
};

/**
 * Deep clones profile data to prevent mutation issues
 */
export const cloneProfileData = (data) => {
  try {
    return JSON.parse(JSON.stringify(data));
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('Failed to deep clone profile data:', error);
    }
    return { ...data };
  }
};
